import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';

import { exists, isValidArray, getKey } from '../../../helpers';
import ContinueBuyButton from '../../base/ContinueBuyButton';
import InputsField from '../../base/InputsField';
import OptionsInput from '../../base/OptionsInput';
import Text from '../../base/Text';
import Styles from './Invoice.styles';

const Wrapper = styled.div`${Styles.wrapper}`; // prettier-ignore
const ContinueWrapper = styled.div`${Styles.continueWrapper}`; // prettier-ignore
const ContinueButton = styled(ContinueBuyButton)`${Styles.continueButton}`; // prettier-ignore
const TitleWrapper = styled.div`${Styles.titleWrapper}`; // prettier-ignore
const Title = styled(Text).attrs({ bold: true })`${Styles.registerTitle}`; // prettier-ignore
const Instructions = styled.p`${Styles.instructions}`; // prettier-ignore
const InvoiceTerms = styled.p`${Styles.invoiceTerms}`; // prettier-ignore
const ErrorWrapper = styled.div`${Styles.errorWrapper}`; // prettier-ignore
const SuccessMessage = styled.div`${Styles.successMessage}`; // prettier-ignore

/**
 * SignUp component
 */
class Invoice extends PureComponent {
  /**
   * Component props
   * @property {Object} translations - translated words
   */
  static propTypes = {
    translations: PropTypes.object,
    locale: PropTypes.string,
    endPoint: PropTypes.string,
    user: PropTypes.object,
    invoiceSettings: PropTypes.object,
  };

  /**
   * Constructor method
   * @param {Object} props - component props
   */
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handlePayment = this.handlePayment.bind(this);
    this.handleBusinessType = this.handleBusinessType.bind(this);
    this.changeHandler = this.changeHandler.bind(this);

    this.emailRef = React.createRef();
    this.rfcRef = React.createRef();
    this.ticketNumberRef = React.createRef();
    this.ticketDateRef = React.createRef();
    this.ticketPriceRef = React.createRef();
    this.saleIdRef = React.createRef();
    this.purchaseTicketRef = React.createRef();
    this.paymentMethodRef = React.createRef();

    this.nonDigit = /[^\d]/g;
    this.phoneCheck = /^[+#*\\[\]]*([0-9]*){6,45}$/;

    this.currentYear = parseInt(
      new Date()
        .getFullYear()
        .toString()
        .substr(-2),
      10
    );

    const { user, invoiceSettings } = props;
    const email = getKey(user, 'email', '');
    const rfc = getKey(user, 'rfc', '');
    const ticketPrice = getKey(invoiceSettings, 'state.ticketPrice', '');
    const ticketDate = getKey(invoiceSettings, 'state.ticketDate', '');
    const transactionID = getKey(invoiceSettings, 'state.transactionID', '');
    const ticketsIDString = getKey(invoiceSettings, 'state.ticketsIDString', '');

    this.requiredField = [
      'email',
      'rfc',
      'ticketNumber',
      'ticketDate',
      'ticketPrice',
      'saleId',
      'paymentMethod',
      'file'
    ];

    this.initialState = {
      error: false,
      success: false,
      email: {
        value: email,
        status: exists(email) ? 1 : 0
      },
      rfc: {
        value: rfc,
        status: 0
      },
      company: {
        value: '',
        status: 0
      },
      businessType: {
        value: '',
        status: 0,
        color: null
      },
      zipcode: {
        value: '',
        status: 0
      },
      ticketNumber: {
        value: ticketsIDString,
        status: exists(ticketsIDString) ? 1 : 0
      },
      ticketDate: {
        value: ticketDate,
        status: exists(ticketDate) ? 1 : 0
      },
      ticketPrice: {
        value: ticketPrice,
        status: exists(ticketPrice) ? 1 : 0
      },
      saleId: {
        value: transactionID,
        status: exists(transactionID) ? 1 : 0
      },
      purchaseTicket: {
        value: '',
        status: 0
      },
      paymentMethod: {
        value: exists(ticketPrice) ? 'creditCard' : '',
        status: exists(ticketPrice) ? 1 : 0,
        color: exists(ticketPrice) ? 'green' : null
      },
      file: {
        value: {
          filename: ''
        },
        status: 0
      },
      formPercentage: 0
    };

    this.state = { ...this.initialState };
  }

  /**
   * componentDidMount method
   * Focus the first empty field
   */
  componentDidMount() {
    this.focusField();
  }

  /**
   * validate fields
   * @param {string} value - value to validate
   * @param {string} type - input type
   * @param {string} name - field name
   * @returns {boolean}
   */
  validateField(value, type, name) {
    switch (type) {
      default:
      case 'money':
        return !isNaN(value);
      case 'text':
        return value.length >= 4;
      case 'email': {
        const {
          email: { value: emailValue }
        } = this.state;
        return name === 'email'
          ? value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)
          : value === emailValue;
      }
      case 'tel': {
        const values = value.match(/\d+/g, '');
        const test = values ? values.join('').length === 6 : false;
        return test;
      }
    }
  }

  /**
   * focus the name field
   */
  focusField() {
    const pendingFields = this.requiredField.filter((field) => {
      const {
        [field]: { status: valueStep }
      } = this.state;

      return valueStep !== 1;
    });

    if (isValidArray(pendingFields)) {
      const pendingField = pendingFields[0];
      if (exists(this[`${pendingField}Ref`].current)) {
        this[`${pendingField}Ref`].current.focus();
      }
    }
  }

  /**
   * fill percentage of the form
   */
  formPercentage() {
    const { formPercentage: currentProgress } = this.state;
    const completeSteps = this.requiredField.reduce((acc, step) => {
      const {
        [step]: { status: valueStep }
      } = this.state;
      return acc + (valueStep === 1 ? 1 : 0);
    }, 0);

    const progress = completeSteps / this.requiredField.length;

    if (progress !== currentProgress) {
      this.setState({
        formPercentage: progress
      });
    }
  }

  /**
   * handles input changes
   * @param {Object} event - synthetic event
   */
  handleChange(event) {
    const {
      target: { name, value, type }
    } = event;
    const newState = {};

    if (name === 'ticketDate') {
      const tempVal = value.replace(/[^0-9]/g, '').substring(0, 6);
      let newValue = tempVal;

      if (tempVal.length === 1) {
        if (tempVal === '0' || tempVal === '1' || tempVal === '2' || tempVal === '3') {
          newValue = tempVal;
        } else {
          newValue = '';
        }
      } else if (tempVal.length === 2) {
        const previousVal = tempVal.substring(0, 1);
        const thisVal = tempVal.substring(1, 2);

        if (previousVal === '0') {
          newValue = thisVal === '0' ? '0' : tempVal;
        } else if (previousVal === '3') {
          newValue = thisVal > 1 ? '31' : tempVal;
        } else {
          newValue = tempVal;
        }
      } else if (tempVal.length === 3) {
        const day = tempVal.substring(0, 2);
        const month = tempVal.substring(2, tempVal.length);

        if (month === '0' || month === '1') {
          newValue = `${day} / ${month}`;
        } else {
          newValue = `${day}`;
        }
      } else if (tempVal.length === 4) {
        const day = tempVal.substring(0, 2);
        const previousVal = tempVal.substring(2, 3);
        const thisVal = tempVal.substring(3, tempVal.length);

        let lastMonthDigit;
        if (previousVal === '1') {
          lastMonthDigit = thisVal > 2 ? '2' : thisVal;
        } else {
          lastMonthDigit = thisVal !== '0' ? thisVal : '';
        }

        newValue = `${day} / ${previousVal}${lastMonthDigit}`;
      } else if (tempVal.length >= 5) {
        const day = tempVal.substring(0, 2);
        const month = tempVal.substring(2, 4);
        let year = tempVal.substring(5, tempVal.length);
        year = tempVal.length === 5 && year < 1 ? '1' : year;
        year = tempVal.length === 6 && year < this.currentYear ? this.currentYear : year;

        newValue = `${day} / ${month} / ${year}`;
      }

      Object.assign(newState, {
        [name]: {
          value: newValue,
          status: this.validateField(newValue, type, name) ? 1 : 2
        }
      });
    } else if (name === 'ticketPrice') {
      if (isNaN(value)) return;
      Object.assign(newState, {
        [name]: {
          value,
          status: this.validateField(value, 'money', name) ? 1 : 2
        }
      });
    } else {
      Object.assign(newState, {
        [name]: {
          value,
          status: this.validateField(value, type, name) ? 1 : 2
        }
      });
    }
    this.setState(newState, this.formPercentage);
  }

  /**
   * Handles form submit
   * @param {Object} event - synthethic event
   */
  handleSubmit(event) {
    const { formPercentage } = this.state;

    if (formPercentage === 1) {
      this.sendForm();
    } else {
      this.focusField();
    }

    event.preventDefault();
  }

  /**
   * Handles payment change options
   * @param {Object} event - synthethic event
   */
  handlePayment(event) {
    const {
      target: { value }
    } = event;

    const status = value !== '' ? 1 : 2;
    let color;

    if (status > 0) {
      color = status === 2 ? 'red' : 'green';
    }

    this.setState({
      paymentMethod: {
        value,
        status,
        color
      }
    }, this.formPercentage);
  }

	changeHandler(event){
    if(event.target.files[0].size > 1024 * 1024 * 8){
      alert(this.props.locale === 'en' ? 'File size must not exceed 8MB' : 'El tamaño del archivo no debe exceder 8MB');
      this.setState({
        file: {
          value: {filename:''},
          status: 0
        }
      }, this.formPercentage);  
      return false;
    }
    this.setState({
      file: {
        value: event.target.files[0],
        status: 1
      }
    }, this.formPercentage);
	}

  /**
   * Handles payment change options
   * @param {Object} event - synthethic event
   */
  handleBusinessType(event) {
    const {
      target: { value }
    } = event;

    const status = value !== '' ? 1 : 2;
    let color;

    if (status > 0) {
      color = status === 2 ? 'red' : 'green';
    }

    this.setState({
      businessType: {
        value,
        status,
        color
      }
    }, this.formPercentage);
  }


  /**
   * Sends the form
   */
  sendForm() {
    const { endPoint } = this.props;
    const {
      email: { value: email },
      rfc: { value: rfc },
      company: { value: name },
      businessType: { value: businessType },
      zipcode: { value: zipcode },
      ticketNumber: { value: ticketNumber },
      ticketDate: { value: ticketDate },
      ticketPrice: { value: ticketPrice },
      saleId: { value: saleId },
      paymentMethod: { value: paymentMethod },
      file: {value: file}
    } = this.state;
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('email', email);
    formData.append('rfc', rfc);
    formData.append('businessType', businessType);
    formData.append('zipcode', zipcode);
    formData.append('name', name);
    formData.append('ticketNumber', ticketNumber);
    formData.append('ticketDate', ticketDate);
    formData.append('ticketPrice', ticketPrice);
    formData.append('saleId', saleId);
    formData.append('paymentMethod', paymentMethod);
    axios.post(
      endPoint,
      formData
    ).then(({ data }) => {
      if (!data.error) {
        this.setState({
          success: true,
        });
      }
    }).catch((error) => {
      console.log(error);
      this.setState({
        ...this.initialState,
        error: true,
      });
    });
  }

  /**
   * render method
   * @returns {JSX}
   */
  render() {
    const {
      props: { translations },
      state: {
        email,
        rfc,
        error,
        formPercentage,
        company,
        businessType,
        zipcode,
        file,
        ticketNumber,
        ticketDate,
        ticketPrice,
        saleId,
        purchaseTicket,
        paymentMethod,
        success
      }
    } = this;

    if (success) {
      return (
        <div className="container">
          <div className="row col-12">
            <SuccessMessage>{`${translations.invoice.sendMessage}`}</SuccessMessage>
          </div>
        </div>
      );
    }

    return (
      <Fragment>
        {error && (
          <ErrorWrapper hasError={error}>
            <div className="container">
              <div className="row">
                <p>{translations.invoice.error}</p>
              </div>
            </div>
          </ErrorWrapper>
        )}
        <div className="container">
          <TitleWrapper>
            <div className="row">
              <div className="col-12 col-sm-8 offset-sm-2">
                <Title>{translations.invoice.title}</Title>
              </div>
            </div>
          </TitleWrapper>
          <Wrapper>
            <div className="row">
              <div className="col-12 col-sm-8 offset-sm-2">
                <Instructions>{translations.invoice.instructions}</Instructions>
              </div>
            </div>
            <form onSubmit={this.handleSubmit} className="row">
              <InputsField
                placeHolder={translations.email.placeHolder}
                label={translations.email.label}
                className="col-12 col-sm-8 offset-sm-2"
                reference={this.emailRef}
                type="email"
                onChange={this.handleChange}
                value={email.value}
                name="email"
                status={email.status}
                fixAutofill="#fafafa"
              />
              <InputsField
                {...translations.invoice.rfc}
                className="col-12 col-sm-8 offset-sm-2"
                reference={this.rfcRef}
                onChange={this.handleChange}
                value={rfc.value}
                name="rfc"
                status={rfc.status}
                fixAutofill="#fafafa"
              />
              <InputsField
                {...translations.invoice.company}
                className="col-12 col-sm-8 offset-sm-2"
                onChange={this.handleChange}
                value={company.value}
                name="company"
                status={company.status}
                fixAutofill="#fafafa"
              />
              <OptionsInput
                options={translations.invoice.businessType.options}
                value={businessType.value}
                name={translations.invoice.businessType.label}
                onChange={this.handleBusinessType}
                className="col-12 col-sm-8 offset-sm-2"
                hasBlackTheme
                color={businessType.color}
              />
              <InputsField
                {...translations.invoice.company_zip_code}
                className="col-12 col-sm-8 offset-sm-2"
                onChange={this.handleChange}
                value={zipcode.value}
                name="zipcode"
                status={zipcode.status}
                fixAutofill="#fafafa"
              />
              <InputsField
                {...translations.invoice.ticketNumber}
                className="col-12 col-sm-8 offset-sm-2"
                reference={this.ticketNumberRef}
                onChange={this.handleChange}
                value={ticketNumber.value}
                name="ticketNumber"
                status={ticketNumber.status}
                fixAutofill="#fafafa"
              />
              <InputsField
                {...translations.invoice.ticketDate}
                className="col-12 col-sm-8 offset-sm-2"
                reference={this.ticketDateRef}
                onChange={this.handleChange}
                value={ticketDate.value}
                name="ticketDate"
                type="tel"
                status={ticketDate.status}
                fixAutofill="#fafafa"
              />
              <InputsField
                {...translations.invoice.ticketPrice}
                className="col-12 col-sm-8 offset-sm-2"
                reference={this.ticketPriceRef}
                onChange={this.handleChange}
                value={ticketPrice.value}
                type="money"
                name="ticketPrice"
                status={ticketPrice.status}
                fixAutofill="#fafafa"
              />
              <InputsField
                {...translations.invoice.saleId}
                className="col-12 col-sm-8 offset-sm-2"
                reference={this.saleIdRef}
                onChange={this.handleChange}
                value={saleId.value}
                name="saleId"
                status={saleId.status}
                fixAutofill="#fafafa"
              />
              <InputsField
                {...translations.invoice.receipt}
                className="col-12 col-sm-8 offset-sm-2"
                type="file"
                name="file"
                value={file.value.filename}
                status={file.status}
                accept="image/png, image/jpeg, application/pdf"
                onChange={this.changeHandler}
              />
              { false && (
              <InputsField
                {...translations.invoice.purchaseTicket}
                className="col-12 col-sm-8 offset-sm-2"
                reference={this.purchaseTicketRef}
                onChange={this.handleChange}
                value={purchaseTicket.value}
                name="purchaseTicket"
                status={purchaseTicket.status}
                fixAutofill="#fafafa"
              />
              )}
              <OptionsInput
                options={translations.invoice.paymentMethod.options}
                value={paymentMethod.value}
                name={translations.invoice.paymentMethod.label}
                onChange={this.handlePayment}
                className="col-12 col-sm-8 offset-sm-2"
                hasBlackTheme
                color={paymentMethod.color}
              />
              <div className="col-12 col-sm-8 offset-sm-2">
                <InvoiceTerms>{translations.invoice.terms}</InvoiceTerms>
              </div>
              <ContinueWrapper className="col-12 col-sm-8 offset-sm-2">
                <ContinueButton
                  className="cbx-regular"
                  continueLabel={translations.continueLabel}
                  buyLabel={translations.continueLabel}
                  buttonAction={this.handleSubmit}
                  progress={formPercentage * 100}
                  isBuying
                />
              </ContinueWrapper>
            </form>

          </Wrapper>
        </div>
      </Fragment>
    );
  }
}

export default Invoice;
